import { useState } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axios from "../axios";

const getFileNameFromUrl = (url, index) => {
  const cleanUrl = cleanFileUrl(url);
  const parts = cleanUrl.split("/");
  return parts[parts.length - 1] || `attachment-${index + 1}.png`;
};

const cleanFileUrl = (url) => {
  if (!url) return "";

  return url.replace(/%22/g, "").replace(/"/g, "");
};

const getFileExtension = (url = "") => {
  const cleanUrl = cleanFileUrl(url).split("?")[0];
  return cleanUrl.split(".").pop()?.toLowerCase();
};

const isPdfFile = (url) => {
  return getFileExtension(url) === "pdf";
};

const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "m4v"];

const isVideoAttachment = (attachment) => {
  if (attachment?.type?.toLowerCase() === "video") {
    return true;
  }

  return VIDEO_EXTENSIONS.includes(getFileExtension(attachment?.image));
};

const ManualAttachmentsModal = ({ theme, show, onHide, attachments = [] }) => {
  const { t } = useTranslation();
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const handleClose = () => {
    setSelectedAttachment(null);
    onHide();
  };

  const handleDownload = async (imageUrl, index) => {
    const cleanUrl = cleanFileUrl(imageUrl);
    const fileName = getFileNameFromUrl(cleanUrl, index);

    try {
      setIsDownloading(true);

      const formData = new FormData();
      formData.append("path", cleanUrl);

      const response = await axios.post(
        "https://servroom.thedigitalhotelier.com/api/v1/general/download-file",
        formData,
        {
          responseType: "blob",
        }
      );

      const blobUrl = window.URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download attachment:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      className={`manual-modal ${theme ? "food-modal" : ""}`}
    >
      <Modal.Body>
        {!selectedAttachment && (
          <Button className="close-btn car" onClick={handleClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24.054"
              height="24.625"
              viewBox="0 0 29 29"
            >
              <g
                id="Group_38128"
                data-name="Group 38128"
                transform="translate(-4973 -1330)"
              >
                <circle
                  id="Ellipse_49"
                  data-name="Ellipse 49"
                  className="cls-1w"
                  cx="14.5"
                  cy="14.5"
                  r="10.5"
                  transform="translate(4973 1330)"
                ></circle>

                <path
                  id="Icon_ionic-ios-close-circle"
                  data-name="Icon ionic-ios-close-circle"
                  className="cls-2w"
                  d="M17.539,3.375a14.333,14.333,0,0,0-14.164,14.5,14.333,14.333,0,0,0,14.164,14.5A14.333,14.333,0,0,0,31.7,17.875,14.333,14.333,0,0,0,17.539,3.375Zm3.589,19.749L17.539,19.45,13.95,23.124a1.075,1.075,0,0,1-1.539,0,1.129,1.129,0,0,1,0-1.575L16,17.875,12.411,14.2a1.129,1.129,0,0,1,0-1.575,1.075,1.075,0,0,1,1.539,0L17.539,16.3l3.589-3.674a1.075,1.075,0,0,1,1.539,0,1.138,1.138,0,0,1,0,1.575l-3.589,3.674,3.589,3.674a1.138,1.138,0,0,1,0,1.575A1.063,1.063,0,0,1,21.127,23.124Z"
                  transform="translate(4969.625 1326.625)"
                ></path>
              </g>
            </svg>
          </Button>
        )}
        <div className="body-container maintenance">
          {!selectedAttachment && <h4 className="mb-3">{t("Attachments")}</h4>}

          {!attachments.length ? (
            <p className="text-muted mb-0">{t("No attachments available.")}</p>
          ) : selectedAttachment ? (
            <div>
              <div className="text-center mb-3">
                {isVideoAttachment(selectedAttachment) ? (
                  <video
                    key={selectedAttachment.image}
                    controls
                    playsInline
                    preload="metadata"
                    style={{
                      width: "100%",
                      maxHeight: "70vh",
                      objectFit: "contain",
                      borderRadius: "10px",
                      background: "#000",
                      border: "1px solid #eee",
                    }}
                  >
                    <source src={cleanFileUrl(selectedAttachment.image)} />

                    {t("Your browser does not support video playback.")}
                  </video>
                ) : isPdfFile(selectedAttachment.image) ? (
                  <iframe
                    src={cleanFileUrl(selectedAttachment.image)}
                    title="PDF attachment"
                    style={{
                      width: "100%",
                      height: "70vh",
                      border: "1px solid #eee",
                    }}
                  />
                ) : (
                  <img
                    src={cleanFileUrl(selectedAttachment.image)}
                    alt="attachment"
                    style={{
                      width: "100%",
                      maxHeight: "70vh",
                      objectFit: "contain",
                      borderRadius: "10px",
                      border: "1px solid #eee",
                    }}
                  />
                )}
              </div>

              <Row className="justify-content-center px-5 sm:px-0">
                <Col lg="3" md="6" xs="6" className="p-0 pl-2 btn-manual">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSelectedAttachment(null)}
                    className="reg-btn big laundary-btn cancel"
                  >
                    {t("Back")}
                  </Button>
                </Col>

                <Col lg="3" md="6" xs="6" className="p-0 pl-2 btn-manual">
                  <Button
                    variant="primary"
                    className="reg-btn big laundary-btn"
                    disabled={isDownloading}
                    onClick={() =>
                      handleDownload(
                        selectedAttachment.image,
                        attachments.findIndex(
                          (attachment) =>
                            attachment.id === selectedAttachment.id
                        )
                      )
                    }
                  >
                    {isDownloading ? t("Downloading...") : t("Download")}
                  </Button>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="row m-0">
              {attachments.map((item, index) => {
                const attachmentUrl = cleanFileUrl(item.image);
                const isVideo = isVideoAttachment(item);

                return (
                  <div
                    className="col-6 col-sm-6 col-md-3 mb-2"
                    key={item.id || index}
                  >
                    <div
                      className="p-2 border rounded-3 h-100"
                      style={{
                        background: "#fff",
                        borderRadius: "12px",
                      }}
                    >
                      {isVideo ? (
                        <div
                          role="button"
                          tabIndex={0}
                          className="position-relative"
                          onClick={() => setSelectedAttachment(item)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              setSelectedAttachment(item);
                            }
                          }}
                          style={{
                            width: "100%",
                            height: "150px",
                            overflow: "hidden",
                            cursor: "pointer",
                            background: "#000",
                          }}
                        >
                          <video
                            src={attachmentUrl}
                            muted
                            poster="/general-video.png"
                            playsInline
                            preload="metadata"
                            onLoadedMetadata={(e) => {
                              try {
                                e.currentTarget.currentTime = 0.1;
                              } catch {}
                            }}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              pointerEvents: "none",
                            }}
                          />

                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              pointerEvents: "none",
                            }}
                          >
                            <div
                              style={{
                                width: "48px",
                                height: "48px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingLeft: "3px",
                                borderRadius: "50%",
                                color: "#fff",
                                background: "#222222A8",
                                fontSize: "22px",
                              }}
                            >
                              ▶
                            </div>
                          </div>
                        </div>
                      ) : isPdfFile(item.image) ? (
                        <div
                          onClick={() => setSelectedAttachment(item)}
                          className="d-flex flex-column justify-content-center align-items-center"
                          style={{
                            width: "100%",
                            height: "150px",
                            borderRadius: "10px",
                            cursor: "pointer",
                            background: "#f8f8f8",
                            border: "1px solid #eee",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "42px",
                              color: "#dc3545",
                            }}
                          >
                            <i
                              className="fa fa-file-pdf-o"
                              aria-hidden="true"
                            />
                          </div>

                          <small
                            className="text-center mt-2 px-2"
                            style={{
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontWeight: "600",
                            }}
                          >
                            PDF
                          </small>
                        </div>
                      ) : (
                        <img
                          src={attachmentUrl}
                          alt={`Attachment ${index + 1}`}
                          onClick={() => setSelectedAttachment(item)}
                          style={{
                            width: "100%",
                            height: "150px",
                            objectFit: "cover",
                            // borderRadius: "10px",
                            cursor: "pointer",
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ManualAttachmentsModal;
